import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AppLayout } from "@/components/AppLayout";
import { motion } from "framer-motion";
import { Plus, Trash2, RefreshCw, FolderOpen, Pencil, Users, RotateCcw, CalendarX, Copy, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ConfigurationEditor from "@/pages/ConfigurationEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Configurations = ({ user }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ drive_folder_id: '', drive_folder_name: '', sheet_id: '', sheet_tab_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [processingState, setProcessingState] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);
  const [showClearMonth, setShowClearMonth] = useState(false);
  const [clearMonthValue, setClearMonthValue] = useState('');
  const [clearingMonth, setClearingMonth] = useState(false);
  const [removingDupes, setRemovingDupes] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportConfigId, setExportConfigId] = useState('');
  const [exportMonth, setExportMonth] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportConfigId && exportConfigId !== 'all') params.append('config_id', exportConfigId);
      if (exportMonth) params.append('month', exportMonth);
      const response = await axios.get(
        `${BACKEND_URL}/api/export/csv?${params.toString()}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const disp = response.headers['content-disposition'] || '';
      const match = disp.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : 'transactions.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
      setShowExport(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleRemoveDuplicatesAll = async () => {
    if (removingDupes) return;
    if (!window.confirm('Scan all households and remove duplicate transactions (same vendor + date + amount)? The oldest copy is kept, extras are deleted. Files stay in Drive.')) return;
    setRemovingDupes(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/remove-duplicates-all`,
        {},
        { withCredentials: true }
      );
      if (data.total_deleted === 0) {
        toast.info(`No duplicates found across ${data.configs_touched} households.`, { duration: 6000 });
      } else {
        toast.success(`Removed ${data.total_deleted} duplicate transaction${data.total_deleted === 1 ? '' : 's'} across ${data.configs_touched} households (${data.total_duplicate_groups} duplicate groups).`, { duration: 8000 });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to remove duplicates');
    } finally {
      setRemovingDupes(false);
    }
  };

  const handleClearMonthAll = async () => {
    if (!clearMonthValue || !/^\d{4}-\d{2}$/.test(clearMonthValue)) {
      toast.error('Pick a month first');
      return;
    }
    setClearingMonth(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/clear-month-all`,
        { month: clearMonthValue },
        { withCredentials: true }
      );
      const parts = [`${data.total_logs_deleted} transaction${data.total_logs_deleted === 1 ? '' : 's'}`];
      if (data.total_payment_marks_deleted > 0) parts.push(`${data.total_payment_marks_deleted} payment mark${data.total_payment_marks_deleted === 1 ? '' : 's'}`);
      toast.success(`Cleared ${parts.join(' + ')} across ${data.configs_touched} household${data.configs_touched === 1 ? '' : 's'} for ${clearMonthValue}. Click "Process Drive Invoices" on each to re-scan.`, { duration: 8000 });
      setShowClearMonth(false);
      setClearMonthValue('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to clear month');
    } finally {
      setClearingMonth(false);
    }
  };

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/configs`, { withCredentials: true });
      setConfigs(response.data);
    } catch (error) {
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/configs`, formData, { withCredentials: true });
      toast.success('Configuration added');
      setFormData({ drive_folder_id: '', drive_folder_name: '', sheet_id: '', sheet_tab_name: '' });
      setShowAddForm(false);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to add configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (configId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/configs/${configId}`, { withCredentials: true });
      toast.success('Configuration deleted');
      setDeleteConfirm(null);
      loadConfigs();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const handleResetIncomplete = async (configId) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/configs/${configId}/reset-incomplete`,
        {},
        { withCredentials: true }
      );
      const cleared = data?.cleared || 0;
      if (cleared === 0) {
        toast.info('Nothing to retry — no incomplete records found for this folder.');
        return;
      }
      toast.success(`Cleared ${cleared} incomplete record${cleared === 1 ? '' : 's'}. Click "Process Drive Invoices" to scan them.`, { duration: 6000 });
      loadConfigs();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reset incomplete records');
    }
  };

  const handleProcessDriveFolder = async (configId) => {
    setProcessingState(prev => ({ ...prev, [configId]: { current: 0, total: 0, fileName: '', status: 'starting' } }));
    try {
      const response = await fetch(`${BACKEND_URL}/api/process-drive-folder/${configId}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to process' }));
        throw new Error(err.detail || 'Failed to process Drive folder');
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'init') {
              setProcessingState(prev => ({ ...prev, [configId]: { current: 0, total: event.to_process, fileName: '', status: 'init', skipped: event.to_skip } }));
            } else if (event.type === 'progress') {
              setProcessingState(prev => ({ ...prev, [configId]: { current: event.current, total: event.total, fileName: event.file_name, status: event.status } }));
            } else if (event.type === 'complete') {
              setProcessingState(prev => { const next = { ...prev }; delete next[configId]; return next; });
              if (event.status === 'no_files') toast.warning(event.message || 'No files found');
              else {
                const parts = [`${event.processed} processed`, `${event.skipped} skipped`];
                if (event.duplicates > 0) parts.push(`${event.duplicates} duplicate${event.duplicates === 1 ? '' : 's'} (re-upload of existing)`);
                if (event.errors > 0) parts.push(`${event.errors} error${event.errors === 1 ? '' : 's'}`);
                toast.success('Done! ' + parts.join(', '), { duration: 6000 });
              }
            } else if (event.type === 'error') {
              setProcessingState(prev => { const next = { ...prev }; delete next[configId]; return next; });
              toast.error(event.message || 'Processing failed');
            }
          } catch (e) { /* ignore */ }
        }
      }
      loadConfigs();
    } catch (error) {
      setProcessingState(prev => { const next = { ...prev }; delete next[configId]; return next; });
      toast.error(error.message || 'Failed to process Drive folder');
    }
  };

  const extractSheetId = (url) => { const m = url.match(/\/d\/([a-zA-Z0-9-_]+)/); return m ? m[1] : url; };
  const extractFolderId = (url) => { const m = url.match(/folders\/([a-zA-Z0-9-_]+)/); return m ? m[1] : url; };

  return (
    <AppLayout user={user} activeNav="/configurations">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-heading font-extrabold text-stone-900">Configurations</h2>
            <p className="text-stone-500 text-sm mt-1">Connect Google Drive folders for invoice scanning</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowExport(true)}
              data-testid="export-csv-button"
              className="rounded-xl border-2 border-stone-200 hover:border-sage-400 hover:bg-sage-50 text-stone-600 hover:text-sage-700"
              title="Export transactions as CSV"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={handleRemoveDuplicatesAll}
              disabled={removingDupes}
              data-testid="remove-duplicates-button"
              className="rounded-xl border-2 border-stone-200 hover:border-amber-400 hover:bg-amber-50 text-stone-600 hover:text-amber-700"
              title="Scan all households and remove duplicate transactions"
            >
              <Copy className="w-4 h-4 mr-2" /> {removingDupes ? 'Scanning...' : 'Remove Duplicates'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowClearMonth(true)}
              data-testid="clear-month-button"
              className="rounded-xl border-2 border-stone-200 hover:border-red-400 hover:bg-red-50 text-stone-600 hover:text-red-600"
              title="Clear a specific month across all households"
            >
              <CalendarX className="w-4 h-4 mr-2" /> Clear a Month
            </Button>
            <Button onClick={() => setShowAddForm(!showAddForm)} data-testid="add-config-button" className="bg-gradient-to-r from-sage-500 to-sage-600 hover:from-sage-600 hover:to-sage-700 text-white rounded-xl shadow-lg shadow-sage-500/20">
              <Plus className="w-4 h-4 mr-2" /> Add Configuration
            </Button>
          </div>
        </div>

        {showAddForm && (
          <Card className="mb-8 border-2 border-sage-100 bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="font-heading">New Configuration</CardTitle>
              <CardDescription>Set up a Google Drive folder to scan invoices from</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Drive Folder ID/URL</Label>
                    <Input data-testid="input-folder-id" placeholder="Folder ID or full URL" value={formData.drive_folder_id} onChange={(e) => setFormData({ ...formData, drive_folder_id: extractFolderId(e.target.value) })} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" required />
                  </div>
                  <div>
                    <Label>Folder Display Name</Label>
                    <Input data-testid="input-folder-name" placeholder="e.g., Monthly Expenses" value={formData.drive_folder_name} onChange={(e) => setFormData({ ...formData, drive_folder_name: e.target.value })} className="border-2 border-stone-200 rounded-xl focus:border-sage-500" required />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-config-button" className="bg-sage-500 hover:bg-sage-600 rounded-xl">{submitting ? 'Adding...' : 'Add Configuration'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-sage-500 border-t-transparent mx-auto"></div>
          </div>
        ) : configs.length === 0 ? (
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardContent className="py-12 text-center">
              <FolderOpen className="w-14 h-14 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">No configurations yet</p>
              <p className="text-sm text-stone-400 mt-1">Add a configuration to start processing invoices</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {configs.map((config, i) => (
              <motion.div key={config.config_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card data-testid={`config-card-${config.config_id}`} className="bg-white/90 backdrop-blur-sm border border-stone-200/60 hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between font-heading">
                      <span className="truncate text-base">{config.drive_folder_name}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditingConfig(config)} data-testid={`edit-config-${config.config_id}`} className="text-stone-400 hover:text-sage-600 hover:bg-sage-50 rounded-lg">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(config.config_id)} data-testid={`delete-button-${config.config_id}`} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardTitle>
                    {config.bill_splitting_enabled && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <Users className="w-3 h-3 text-sage-600" />
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-sage-600">
                          Split · {(config.members || []).filter(m => m.is_active !== false).length} members · {(config.split_rules || []).length} rules
                        </span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div>
                      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Folder ID</p>
                      <p className="text-xs text-stone-600 font-mono bg-stone-50 px-2 py-1 rounded-lg truncate">{config.drive_folder_id}</p>
                    </div>
                    <div className="pt-3 border-t border-stone-100">
                      {processingState[config.config_id] ? (
                        <div className="space-y-2" data-testid={`processing-status-${config.config_id}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-sage-700">
                              {processingState[config.config_id].status === 'starting' ? 'Starting...' :
                               processingState[config.config_id].status === 'init' ? 'Scanning folder...' :
                               `Processing ${processingState[config.config_id].current} of ${processingState[config.config_id].total}`}
                            </span>
                            {processingState[config.config_id].total > 0 && (
                              <span className="text-xs text-stone-400">
                                {Math.round((processingState[config.config_id].current / processingState[config.config_id].total) * 100)}%
                              </span>
                            )}
                          </div>
                          {processingState[config.config_id].total > 0 && (
                            <div className="w-full bg-stone-100 rounded-full h-1.5">
                              <div className="bg-sage-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(processingState[config.config_id].current / processingState[config.config_id].total) * 100}%` }} />
                            </div>
                          )}
                          {processingState[config.config_id].fileName && (
                            <p className="text-[10px] text-stone-400 truncate">{processingState[config.config_id].fileName}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button onClick={() => handleProcessDriveFolder(config.config_id)} className="flex-1 bg-gradient-to-r from-sage-500 to-sage-600 hover:from-sage-600 hover:to-sage-700 text-white rounded-xl shadow-sm" data-testid={`process-drive-${config.config_id}`}>
                            <RefreshCw className="w-4 h-4 mr-2" /> Process Drive Invoices
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleResetIncomplete(config.config_id)}
                            title="Re-scan failed / incomplete files"
                            className="rounded-xl border-2 border-stone-200 hover:border-sage-400 hover:bg-sage-50 text-stone-500 hover:text-sage-700"
                            data-testid={`reset-incomplete-${config.config_id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Configuration?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-red-500 hover:bg-red-600 rounded-xl">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showExport} onOpenChange={(o) => { setShowExport(o); if (!o) { setExportConfigId(''); setExportMonth(''); } }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Export Transactions as CSV</AlertDialogTitle>
            <AlertDialogDescription>
              Downloads a CSV with columns: Date, Platform, Description, Household, Amount, Deducted, Final Amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Household</Label>
              <select
                value={exportConfigId}
                onChange={(e) => setExportConfigId(e.target.value)}
                className="mt-1 w-full border-2 border-stone-200 rounded-xl px-3 py-2 text-sm focus:border-sage-400 outline-none"
                data-testid="export-household-select"
              >
                <option value="all">All households</option>
                {configs.map((c) => (
                  <option key={c.config_id} value={c.config_id}>{c.drive_folder_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Month (optional)</Label>
              <Input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                className="mt-1 border-2 border-stone-200 rounded-xl focus:border-sage-400"
                data-testid="export-month-input"
              />
              <p className="text-[11px] text-stone-400 mt-1.5">Leave blank to export all months</p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={exporting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExportCSV}
              disabled={exporting}
              className="bg-sage-500 hover:bg-sage-600 rounded-xl"
              data-testid="export-csv-confirm"
            >
              {exporting ? 'Preparing...' : 'Download CSV'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showClearMonth} onOpenChange={(o) => { setShowClearMonth(o); if (!o) setClearMonthValue(''); }}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Clear a Month</AlertDialogTitle>
            <AlertDialogDescription>
              Deletes every transaction AND payment mark in the selected month, across <strong>all households you have access to</strong>. Files stay in Google Drive — the next Process Drive Invoices will re-scan them fresh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Month</Label>
            <Input
              type="month"
              value={clearMonthValue}
              onChange={(e) => setClearMonthValue(e.target.value)}
              className="mt-1 border-2 border-stone-200 rounded-xl focus:border-red-400"
              data-testid="clear-month-input"
            />
            <p className="text-[11px] text-stone-400 mt-1.5">e.g., 2026-06 for June 2026</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={clearingMonth}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearMonthAll}
              disabled={clearingMonth || !clearMonthValue}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
              data-testid="clear-month-confirm"
            >
              {clearingMonth ? 'Clearing...' : 'Clear this month'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfigurationEditor
        open={!!editingConfig}
        onOpenChange={(o) => { if (!o) setEditingConfig(null); }}
        config={editingConfig}
        onSaved={loadConfigs}
      />
    </AppLayout>
  );
};

export default Configurations;
